/**
 * Unified Bridge API Client
 * All database operations go through MySQL via Bridge API
 * Replaces Supabase client for all CRUD operations
 */

import { getApiUrl } from './apiConfig';
import { shouldIntercept, interceptAuth } from './authBridge';
import { apiFetch, apiErrorFromResponse, getUserErrorMessage, ApiError } from './apiError';
// supabaseDataBridge removed — all data from PHP/MySQL
// authMode removed — always using PHP/MySQL bridgeapi
// ============= Types =============
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  total?: number;
  page?: number;
  limit?: number;
}

export interface AdminUser {
  id: number;
  name: string;
  email: string;
  phone?: string;
  role: 'super_admin' | 'admin' | 'moderator' | 'data_entry' | 'support' | 'viewer';
  is_active: boolean;
  login_count: number;
  last_login_at?: string;
  custom_permissions?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface BloodBank {
  id: number;
  name: string;
  license_no?: string;
  address?: string;
  area?: string;
  city?: string;
  state?: string;
  pincode?: string;
  phone?: string;
  mobile?: string;
  email?: string;
  contact_person?: string;
  logo_url?: string;
  gallery?: any[];
  latitude?: number;
  longitude?: number;
  verification_status: 'pending' | 'approved' | 'rejected'; // MySQL ENUM values
  rejection_reason?: string;
  subscription_plan?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface BloodStock {
  id: number;
  blood_bank_id: number;
  blood_group: string;
  units_available: number;
  units_reserved: number;
  expiry_date?: string;
  notes?: string;
  last_updated: string;
}

export interface AmbulanceProvider {
  id: number;
  provider_name: string;
  provider_type: string;
  contact_number: string;
  alt_contact?: string;
  email?: string;
  city: string;
  state?: string;
  pincode?: string;
  base_location?: string;
  kyc_status: 'pending' | 'approved' | 'rejected';
  is_verified: boolean;
  is_active: boolean;
  created_at: string;
}

export interface Ambulance {
  id: number;
  provider_id: number;
  vehicle_number: string;
  ambulance_type: string;
  base_charge: number;
  per_km_charge: number;
  current_city?: string;
  is_available: boolean;
  is_active: boolean;
}

export interface HomecareProvider {
  id: number;
  provider_name: string;
  provider_type: string;
  gender?: string;
  age?: number;
  contact_number: string;
  city: string;
  experience_years: number;
  expected_charge: number;
  kyc_status: 'pending' | 'approved' | 'rejected';
  is_active: boolean;
}

export interface HealthCampOrganizer {
  id: number;
  organizer_name: string;
  organizer_type: string;
  contact_person?: string;
  contact_number: string;
  email?: string;
  city?: string;
  kyc_status: 'pending' | 'approved' | 'rejected';
  is_active: boolean;
}

export interface HealthCamp {
  id: number;
  organizer_id?: number;
  camp_name: string;
  description?: string;
  camp_date: string;
  start_time: string;
  end_time: string;
  city: string;
  area?: string;
  full_address: string;
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
  is_featured: boolean;
  is_active: boolean;
}

export interface Ad {
  id: number;
  title: string;
  description?: string;
  ad_type: string;
  image_url?: string;
  link_url?: string;
  placement: string;
  priority: number;
  impressions: number;
  clicks: number;
  is_active: boolean;
}

export interface Task {
  id: number;
  title: string;
  description?: string;
  status: 'todo' | 'in_progress' | 'review' | 'done' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assigned_to?: number;
  due_date?: string;
  created_at: string;
}

export interface SubscriptionPlan {
  id: number;
  name: string;
  description?: string;
  plan_type: string;
  duration_months: number;
  base_price: number;
  discount_percent: number;
  final_price: number;
  features?: any;
  is_featured: boolean;
  is_active: boolean;
}

// ============= API Client =============
class BridgeApiClient {
  // Always get fresh URL (supports dev mode toggle without restart)
  private get baseUrl(): string {
    return getApiUrl();
  }

  // Get base URL (public for invoice downloads etc)
  getBaseUrl(): string {
    return this.baseUrl;
  }

  // Admin bearer token, if logged in — many admin_* GET reads and all admin mutations
  // require this server-side (requireAdminAuth()), so both call() and post() send it.
  //
  // Exception: if this specific request already carries its own member/provider
  // identity (a `token` or `provider_id` param — e.g. member self-serve ad
  // booking, AI profile, called from a member dashboard opened via admin's
  // "view as member" impersonation in the same browser tab), don't let a
  // still-logged-in admin session's bearer token silently override it —
  // getBearerToken() on the server prefers the Authorization header over
  // body/query params, so an admin token here would hijack the request's
  // intended identity even though the caller explicitly chose to authenticate
  // as the member instead.
  private getAuthHeaders(params?: Record<string, any>): Record<string, string> {
    if (params && (params.token || params.provider_id)) {
      return {};
    }
    try {
      const adminAuth = localStorage.getItem('patakare_admin_auth');
      if (adminAuth) {
        const parsed = JSON.parse(adminAuth);
        if (parsed?.token) {
          return { Authorization: `Bearer ${parsed.token}` };
        }
      }
    } catch {
      // Ignore parse errors
    }
    return {};
  }

  // Generic API call - returns data directly or throws error
  async call<T>(action: string, params: Record<string, any> = {}): Promise<T> {
    // Supabase auth interception for verify/forgot-password style GET actions
    if (shouldIntercept(action)) {
      try {
        const result = await interceptAuth(action, params);
        return (result?.data ?? result) as T;
      } catch (err) {
        console.error(`[BridgeAPI:supabase] Error on ${action}:`, err);
        throw err;
      }
    }

    try {
      const url = new URL(this.baseUrl);
      url.searchParams.set('action', action);

      // Add other params
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.set(key, String(value));
        }
      });

      const response = await apiFetch(url.toString(), { headers: this.getAuthHeaders(params) });
      const rawText = await response.text();

      let result: any;
      try {
        result = rawText ? JSON.parse(rawText) : {};
      } catch {
        // Non-JSON response (common during PHP fatal errors)
        throw new ApiError('Server returned an invalid response. Please try again.', 'server', response.status);
      }

      // HTTP-level failures
      if (!response.ok) {
        throw apiErrorFromResponse(response.status, result);
      }

      // Handle API response format
      if (result.success === false) {
        throw new ApiError(result.error || result.message || 'API Error', 'unknown');
      }
      
      // Some endpoints return metadata alongside `data` (e.g. pagination, summary).
      // In those cases, callers need the full object, not just the `data` array.
      if (result && typeof result === 'object') {
        if (typeof result.pagination === 'object' && result.pagination) {
          return result as T;
        }
        // Preserve responses with summary (e.g., duplicate detection)
        if (typeof result.summary === 'object' && result.summary && 'data' in result) {
          return result as T;
        }
        // Some endpoints use totals without a `pagination` object.
        if (
          (typeof result.total === 'number' || typeof result.page === 'number' || typeof result.limit === 'number') &&
          'data' in result
        ) {
          return result as T;
        }
      }

      // Return data from various response formats
      return (result.data ?? result.employees ?? result.tasks ?? result.logs ?? result.items ?? result) as T;
    } catch (error) {
      // 404 = record not found, 401 = stale/invalid token — warn, not error
      if (error instanceof ApiError && (error.status === 404 || error.status === 401)) {
        console.warn(`[BridgeAPI] ${action}: ${error.message}`);
      } else {
        console.error(`[BridgeAPI] Error calling ${action}:`, error);
      }
      throw error;
    }
  }

  // POST request for mutations - returns data directly or throws error
  // timeoutMs is optional and only needed for calls known to run past the
  // default 20s (e.g. AI image generation, which can genuinely take 20-40s) —
  // every existing caller is unaffected since it defaults to apiFetch()'s
  // own default when omitted.
  async post<T>(action: string, body: Record<string, any>, timeoutMs?: number): Promise<T> {
    // Supabase auth interception for login/register/admin_login style POST actions
    if (shouldIntercept(action)) {
      try {
        const result = await interceptAuth(action, body);
        return result as T;
      } catch (err) {
        console.error(`[BridgeAPI:supabase] Error on ${action}:`, err);
        throw err;
      }
    }
    // All mutations go directly to PHP/MySQL bridgeapi
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(body),
      };

      const response = await apiFetch(this.baseUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action, ...body })
      }, timeoutMs);

      const rawText = await response.text();
      let result: any;
      try {
        result = rawText ? JSON.parse(rawText) : {};
      } catch {
        throw new ApiError('Server returned an invalid response. Please try again.', 'server', response.status);
      }

      if (!response.ok) {
        throw apiErrorFromResponse(response.status, result);
      }

      if (result.success === false) {
        throw new ApiError(result.error || result.message || 'API Error', 'unknown');
      }

      return result.data ?? result;
    } catch (error) {
      console.error(`[BridgeAPI] Error posting ${action}:`, error);
      throw error;
    }
  }

  // ============= Admin Users =============
  async getAdminUsers(filters?: { role?: string; is_active?: boolean }) {
    return this.call<AdminUser[]>('admin_users', filters || {});
  }

  async createAdminUser(data: Partial<AdminUser>) {
    return this.post<AdminUser>('admin_create_user', data);
  }

  async updateAdminUser(id: number, data: Partial<AdminUser>) {
    return this.post<AdminUser>('admin_update_user', { id, ...data });
  }

  async toggleAdminUserStatus(id: number, is_active: boolean) {
    return this.post('admin_toggle_user', { id, is_active });
  }

  // ============= Tasks =============
  async getTasks(filters?: { status?: string; assigned_to?: number }) {
    return this.call<Task[]>('admin_tasks', filters || {});
  }

  async createTask(data: Partial<Task>) {
    return this.post<Task>('admin_create_task', data);
  }

  async updateTask(id: number, data: Partial<Task>) {
    return this.post<Task>('admin_update_task', { id, ...data });
  }

  async updateTaskStatus(id: number, status: string) {
    return this.post('admin_task_status', { id, status });
  }

  // ============= Activity Logs =============
  async getActivityLogs(filters?: { admin_id?: number; action_type?: string; limit?: number }) {
    return this.call('admin_activity_logs', filters || {});
  }

  async logActivity(data: { action_type: string; action_description: string; entity_type?: string; entity_id?: string; metadata?: any }) {
    return this.post('admin_log_activity', data);
  }

  // ============= Blood Banks =============
  async getBloodBanks(filters?: { city?: string; verification_status?: string; is_active?: boolean }) {
    return this.call<BloodBank[]>('admin_blood_banks', filters || {});
  }

  async getBloodBankDetail(id: number) {
    return this.call<BloodBank>('blood_bank_detail', { id });
  }

  async createBloodBank(data: Partial<BloodBank>) {
    return this.post<BloodBank>('admin_create_blood_bank', data);
  }

  async updateBloodBank(id: number, data: Partial<BloodBank>) {
    return this.post<BloodBank>('admin_update_blood_bank', { id, ...data });
  }

  async approveBloodBank(id: number, comments?: string) {
    return this.post('admin_approve_blood_bank', { id, comments });
  }

  async rejectBloodBank(id: number, reason: string) {
    return this.post('admin_reject_blood_bank', { id, reason });
  }

  // ============= Blood Stock =============
  async getBloodStock(blood_bank_id?: number) {
    return this.call<BloodStock[]>('blood_stock', { blood_bank_id });
  }

  async updateBloodStock(id: number, data: Partial<BloodStock>) {
    return this.post('admin_update_blood_stock', { id, ...data });
  }

  // ============= Ambulance Providers =============
  async getAmbulanceProviders(filters?: { city?: string; kyc_status?: string }) {
    return this.call<AmbulanceProvider[]>('admin_ambulance_providers', filters || {});
  }

  async createAmbulanceProvider(data: Partial<AmbulanceProvider>) {
    return this.post<AmbulanceProvider>('admin_create_ambulance_provider', data);
  }

  async updateAmbulanceProvider(id: number, data: Partial<AmbulanceProvider>) {
    return this.post<AmbulanceProvider>('admin_update_ambulance_provider', { id, ...data });
  }

  async approveAmbulanceProvider(id: number) {
    return this.post('admin_approve_ambulance_provider', { id });
  }

  // ============= Ambulances =============
  async getAmbulances(filters?: { provider_id?: number; city?: string }) {
    return this.call<Ambulance[]>('admin_ambulances', filters || {});
  }

  async createAmbulance(data: Partial<Ambulance>) {
    return this.post<Ambulance>('admin_create_ambulance', data);
  }

  async updateAmbulance(id: number, data: Partial<Ambulance>) {
    return this.post<Ambulance>('admin_update_ambulance', { id, ...data });
  }

  // ============= Ambulance Bookings =============
  async getAmbulanceBookings(filters?: { status?: string; provider_id?: number }) {
    return this.call('admin_ambulance_bookings', filters || {});
  }

  async updateAmbulanceBookingStatus(id: number, status: string, notes?: string) {
    return this.post('admin_ambulance_booking_status', { id, status, notes });
  }

  // ============= Homecare Providers =============
  async getHomecareProviders(filters?: { city?: string; provider_type?: string; kyc_status?: string }) {
    return this.call<HomecareProvider[]>('admin_homecare_providers', filters || {});
  }

  async createHomecareProvider(data: Partial<HomecareProvider>) {
    return this.post<HomecareProvider>('admin_create_homecare_provider', data);
  }

  async updateHomecareProvider(id: number, data: Partial<HomecareProvider>) {
    return this.post<HomecareProvider>('admin_update_homecare_provider', { id, ...data });
  }

  async approveHomecareProvider(id: number) {
    return this.post('admin_approve_homecare_provider', { id });
  }

  // ============= Homecare Bookings =============
  async getHomecareBookings(filters?: { status?: string }) {
    return this.call('admin_homecare_bookings', filters || {});
  }

  async updateHomecareBookingStatus(id: number, status: string, notes?: string) {
    return this.post('admin_homecare_booking_status', { id, status, notes });
  }

  // ============= Health Camp Organizers =============
  async getHealthCampOrganizers(filters?: { kyc_status?: string }) {
    return this.call<HealthCampOrganizer[]>('admin_health_camp_organizers', filters || {});
  }

  async createHealthCampOrganizer(data: Partial<HealthCampOrganizer>) {
    return this.post<HealthCampOrganizer>('admin_create_camp_organizer', data);
  }

  async updateHealthCampOrganizer(id: number, data: Partial<HealthCampOrganizer>) {
    return this.post<HealthCampOrganizer>('admin_update_camp_organizer', { id, ...data });
  }

  // ============= Health Camps =============
  async getHealthCamps(filters?: { city?: string; status?: string }) {
    return this.call<HealthCamp[]>('admin_health_camps', filters || {});
  }

  async createHealthCamp(data: Partial<HealthCamp>) {
    return this.post<HealthCamp>('admin_create_health_camp', data);
  }

  async updateHealthCamp(id: number, data: Partial<HealthCamp>) {
    return this.post<HealthCamp>('admin_update_health_camp', { id, ...data });
  }

  // ============= Health Camp Registrations =============
  async getHealthCampRegistrations(camp_id: number) {
    return this.call('admin_camp_registrations', { camp_id });
  }

  // ============= Patient Users =============
  async getPatientUsers(filters?: { city?: string; is_active?: boolean }) {
    return this.call('admin_patient_users', filters || {});
  }

  async getPatientFiles(patient_id: number) {
    return this.call('admin_patient_files', { patient_id });
  }

  // ============= Ads =============
  async getAds(filters?: { placement?: string; is_active?: boolean }) {
    return this.call<Ad[]>('admin_ads', filters || {});
  }

  async createAd(data: Partial<Ad>) {
    return this.post<Ad>('admin_create_ad', data);
  }

  async updateAd(id: number, data: Partial<Ad>) {
    return this.post<Ad>('admin_update_ad', { id, ...data });
  }

  async toggleAdStatus(id: number, is_active: boolean) {
    return this.post('admin_toggle_ad', { id, is_active });
  }

  // ============= Campaigns =============
  async getCampaigns(filters?: { status?: string; campaign_type?: string }) {
    return this.call('admin_campaigns', filters || {});
  }

  async createCampaign(data: any) {
    return this.post('admin_create_campaign', data);
  }

  async updateCampaign(id: number, data: any) {
    return this.post('admin_update_campaign', { id, ...data });
  }

  // ============= Subscription Plans =============
  async getSubscriptionPlans(filters?: { plan_type?: string; category?: string; is_active?: boolean }) {
    return this.call<SubscriptionPlan[]>('admin_subscription_plans', filters || {});
  }

  async createSubscriptionPlan(data: Partial<SubscriptionPlan>) {
    const { subscriptionPlanWriteSchema, subscriptionPlanResponseSchema, validateWith, formatErrors } =
      await import('@/lib/subscriptionSchemas');
    const v = validateWith(subscriptionPlanWriteSchema, data);
    if (!v.ok) throw new Error(`Plan create validation failed → ${formatErrors(v.errors)}`);
    const res = await this.post<SubscriptionPlan>('subscription_plan_create', v.data as any);
    const r = validateWith(subscriptionPlanResponseSchema, res);
    if (!r.ok) console.warn('[bridgeApi] subscription_plan_create response shape unexpected:', r.errors);
    return res;
  }

  async updateSubscriptionPlan(id: number, data: Partial<SubscriptionPlan>) {
    const { subscriptionPlanWriteSchema, subscriptionPlanResponseSchema, validateWith, formatErrors } =
      await import('@/lib/subscriptionSchemas');
    const v = validateWith(subscriptionPlanWriteSchema, { id, ...data });
    if (!v.ok) throw new Error(`Plan update validation failed → ${formatErrors(v.errors)}`);
    const res = await this.post<SubscriptionPlan>('subscription_plan_update', v.data as any);
    const r = validateWith(subscriptionPlanResponseSchema, res);
    if (!r.ok) console.warn('[bridgeApi] subscription_plan_update response shape unexpected:', r.errors);
    return res;
  }

  // ============= Member Subscriptions =============
  async getMemberSubscriptions(filters?: { member_type?: number; status?: string }) {
    return this.call('admin_member_subscriptions', filters || {});
  }

  async getMemberPayments(filters?: { member_id?: number; status?: string }) {
    return this.call('admin_member_payments', filters || {});
  }

  // ============= Blood Bank Subscriptions =============
  async getBloodBankSubscriptions(filters?: { blood_bank_id?: number; status?: string }) {
    return this.call('admin_blood_bank_subscriptions', filters || {});
  }

  // ============= API Keys =============
  async getApiKeys() {
    return this.call('admin_api_keys');
  }

  async createApiKey(data: { name: string; description?: string; permissions?: string[] }) {
    return this.post('admin_create_api_key', data);
  }

  async toggleApiKey(id: number, is_active: boolean) {
    return this.post('admin_toggle_api_key', { id, is_active });
  }

  async deleteApiKey(id: number) {
    return this.post('admin_delete_api_key', { id });
  }

  // ============= API Request Logs =============
  async getApiRequestLogs(filters?: { api_key_id?: number; limit?: number }) {
    return this.call('admin_api_logs', filters || {});
  }

  // ============= Feature Flags =============
  async getFeatureFlags(filters?: { category?: string }) {
    return this.call('admin_feature_flags', filters || {});
  }

  async updateFeatureFlag(id: number, data: { is_enabled?: boolean; config?: any }) {
    return this.post('admin_update_feature_flag', { id, ...data });
  }

  async createFeatureFlag(data: { key: string; name: string; description?: string; category?: string }) {
    return this.post('admin_create_feature_flag', data);
  }

  // ============= AI Configurations =============
  async getAiConfigurations() {
    return this.call('admin_ai_configs');
  }

  async createAiConfiguration(data: any) {
    return this.post('admin_create_ai_config', data);
  }

  async updateAiConfiguration(id: number, data: any) {
    return this.post('admin_update_ai_config', { id, ...data });
  }

  async setDefaultAiConfiguration(id: number) {
    return this.post('admin_set_default_ai', { id });
  }

  // ============= AI Training Data =============
  async getAiTrainingData(filters?: { category?: string }) {
    return this.call('admin_ai_training', filters || {});
  }

  async createAiTrainingData(data: { title: string; content: string; category?: string }) {
    return this.post('admin_create_ai_training', data);
  }

  async updateAiTrainingData(id: number, data: any) {
    return this.post('admin_update_ai_training', { id, ...data });
  }

  // ============= Managed Listings =============
  async getManagedListings(filters?: { status?: string; member_type?: number }) {
    return this.call('admin_managed_listings', filters || {});
  }

  async approveListings(id: number) {
    return this.post('admin_approve_listing', { id });
  }

  async rejectListing(id: number, reason: string) {
    return this.post('admin_reject_listing', { id, reason });
  }

  // ============= System Error Logs =============
  async getSystemErrorLogs(filters?: { severity?: string; is_resolved?: boolean }) {
    return this.call('admin_error_logs', filters || {});
  }

  async resolveErrorLog(id: number, notes?: string) {
    return this.post('admin_resolve_error', { id, notes });
  }

  // ============= Patient Card Settings =============
  async getPatientCardSettings() {
    return this.call('admin_patient_card_settings');
  }

  async updatePatientCardSettings(key: string, value: any) {
    return this.post('admin_update_patient_card_settings', { key, value });
  }

  // ============= Booking Inquiries =============
  async getInquiries(table: string, providerId?: string | number, providerIdField?: string, token?: string) {
    // Member inquiries
    if (providerIdField === 'member_id' || !providerIdField) {
      return this.call('inquiries', { member_id: providerId, member_type: 1, token: token || '' });
    }
    // Fallback for legacy/admin endpoints
    return this.call('get_inquiries', { table, provider_id: providerId, provider_id_field: providerIdField });
  }

  // Member inquiries with explicit member_type
  // token: the calling member's own auth token — required since getInquiries()
  // now verifies the caller actually owns memberId (Progress Tracker B12).
  async getMemberInquiries(memberId: number, memberType: number, status?: string, token?: string) {
    return this.call<{ inquiries: any[]; total: number; today: number }>('inquiries', {
      member_id: memberId,
      member_type: memberType,
      status: status || '',
      token: token || '',
    });
  }

  // Create inquiry (member_inquiries)
  async createInquiry(_table: string, data: Record<string, any>) {
    // Backend action create_inquiry writes into member_inquiries and expects these keys.
    return this.post('create_inquiry', data);
  }

  // Update inquiry status (member_inquiries)
  // token: required — updateInquiryStatus() now verifies the caller owns this inquiry (Progress Tracker B12).
  async updateInquiryStatus(_table: string, id: string, status: string, notes?: string, token?: string) {
    // Backend expects: id, status, admin_notes
    return this.post('update_inquiry_status', {
      id,
      status,
      admin_notes: notes || '',
      token: token || '',
    });
  }

  async getTodayInquiriesCount(table: string, providerId: string | number, providerIdField: string) {
    // Use direct inquiries endpoint which returns today count
    if (providerIdField === 'member_id' || !providerIdField) {
      const response = await this.call<{ today: number }>('inquiries', {
        member_id: providerId,
        member_type: 1,
        limit: 1, // Just need counts
      });
      return { count: response?.today || 0 };
    }
    return this.call('get_today_inquiries_count', { table, provider_id: providerId, provider_id_field: providerIdField });
  }

  // ============= Reviews Management =============
  private get reviewsUrl() {
    return this.baseUrl.replace('api.php', 'reviews.php');
  }

  // Shared GET/POST + timeout + parse + status handling for all reviews.php calls.
  // Throws ApiError on any failure (network/timeout/non-JSON/HTTP/API-level) — callers decide
  // whether to let it propagate (getReviews/getReviewers/searchMembers) or catch it and
  // return { success: false, message } (mutation methods below).
  private async reviewsFetch<T = any>(
    action: string,
    options: RequestInit = {},
    params?: Record<string, string | number>
  ): Promise<T> {
    const url = new URL(this.reviewsUrl);
    url.searchParams.set('action', action);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          url.searchParams.set(key, String(value));
        }
      });
    }

    // Admin-only reviews.php actions (adminListReviews, adminUpdateReview, etc.) require this
    // server-side; public actions (register_reviewer/submit_review) simply ignore it.
    const response = await apiFetch(url.toString(), {
      ...options,
      headers: { ...this.getAuthHeaders(), ...(options.headers as Record<string, string> | undefined) },
    });
    const rawText = await response.text();

    let result: any;
    try {
      result = rawText ? JSON.parse(rawText) : {};
    } catch {
      throw new ApiError('Server returned an invalid response. Please try again.', 'server', response.status);
    }

    if (!response.ok) {
      throw apiErrorFromResponse(response.status, result);
    }

    return result as T;
  }

  async getReviews(filters?: {
    status?: string;
    search?: string;
    member_type?: number;
    page?: number;
    limit?: number;
  }) {
    try {
      const data = await this.reviewsFetch('admin_list', {}, filters as Record<string, string | number> | undefined);
      if (data.success === false) {
        throw new ApiError(data.message || 'Failed to fetch reviews', 'unknown');
      }
      return data;
    } catch (error) {
      console.error('[BridgeAPI] Error fetching reviews:', error);
      throw error;
    }
  }

  async getReviewers(limit = 50) {
    try {
      return await this.reviewsFetch('admin_reviewers', {}, { limit });
    } catch (error) {
      console.error('[BridgeAPI] Error fetching reviewers:', error);
      throw error;
    }
  }

  async searchMembers(search: string) {
    try {
      return await this.reviewsFetch('admin_search_members', {}, { search });
    } catch (error) {
      console.error('[BridgeAPI] Error searching members:', error);
      throw error;
    }
  }

  async updateReview(data: {
    review_id: number;
    rating?: number;
    title?: string;
    comment?: string;
    moderation_status?: string;
    moderation_reason?: string;
    is_featured?: boolean;
    reply_text?: string;
  }) {
    try {
      return await this.reviewsFetch('admin_update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
    } catch (error) {
      console.error('[BridgeAPI] Error updating review:', error);
      return { success: false, message: getUserErrorMessage(error) };
    }
  }

  async addReview(data: {
    member_id: number;
    member_type: number;
    reviewer_name: string;
    reviewer_mobile: string;
    reviewer_city?: string;
    rating: number;
    title?: string;
    comment: string;
  }) {
    try {
      return await this.reviewsFetch('admin_add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
    } catch (error) {
      console.error('[BridgeAPI] Error adding review:', error);
      return { success: false, message: getUserErrorMessage(error) };
    }
  }

  async deleteReview(reviewId: number) {
    try {
      return await this.reviewsFetch('admin_delete', {}, { review_id: reviewId });
    } catch (error) {
      console.error('[BridgeAPI] Error deleting review:', error);
      return { success: false, message: getUserErrorMessage(error) };
    }
  }

  async moderateReview(reviewId: number, status: 'approved' | 'rejected' | 'flagged') {
    try {
      return await this.reviewsFetch('admin_update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ review_id: reviewId, moderation_status: status }),
      });
    } catch (error) {
      console.error('[BridgeAPI] Error moderating review:', error);
      return { success: false, message: getUserErrorMessage(error) };
    }
  }

  async blockReviewer(reviewerId: number, status: 'active' | 'blocked') {
    try {
      return await this.reviewsFetch('admin_block_reviewer', {}, { reviewer_id: reviewerId, status });
    } catch (error) {
      console.error('[BridgeAPI] Error blocking reviewer:', error);
      return { success: false, message: getUserErrorMessage(error) };
    }
  }

  async submitPublicReview(data: {
    member_id: number;
    member_type: number;
    rating: number;
    comment: string;
    reviewer_name?: string;
  }) {
    try {
      // One persisted anonymous identity per browser, reused across every
      // review submission — previously generated a brand-new `anon<timestamp>`
      // mobile on EVERY call, so register_reviewer() (reviews.php) always
      // inserted a fresh reviewer row instead of hitting its own "Welcome
      // back" existing-mobile path. That silently defeated submitReview()'s
      // 30-day same-reviewer duplicate-review check — the same visitor
      // submitting twice was always treated as two different people, with no
      // trace anywhere that they'd already reviewed once (confirmed live,
      // matches the reported "I can't tell if my review went through, so I
      // might submit it again" experience).
      // reviewers.mobile is varchar(20) — base-36 timestamp keeps this well
      // under that limit (a plain decimal Date.now() alone already overflows it).
      const ANON_REVIEWER_KEY = 'patakare_anon_reviewer_token';
      let anonMobile = localStorage.getItem(ANON_REVIEWER_KEY);
      if (!anonMobile) {
        anonMobile = `anon${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
        localStorage.setItem(ANON_REVIEWER_KEY, anonMobile);
      }
      const regData = await this.reviewsFetch<{ success: boolean; reviewer_id?: number; message?: string }>(
        'register_reviewer',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: data.reviewer_name || 'Anonymous', mobile: anonMobile }),
        }
      );
      if (!regData.success || !regData.reviewer_id) {
        return { success: false, message: regData.message || 'Could not register reviewer' };
      }
      return await this.reviewsFetch('submit_review', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reviewer_id: regData.reviewer_id,
          member_id: data.member_id,
          member_type: data.member_type,
          rating: data.rating,
          comment: data.comment,
        }),
      });
    } catch (error) {
      console.error('[BridgeAPI] Error submitting review:', error);
      return { success: false, message: getUserErrorMessage(error) };
    }
  }
}


// Export singleton instance
export const bridgeApi = new BridgeApiClient();

// Export class for testing
export { BridgeApiClient };
